import * as request from "superagent";
import { identifyMixPanel } from "../../../shared/actions/analytics";
import * as auth from "../../../shared/actions/auth";
import {
  DELETE_ACCOUNT_URL,
  LOGIN_URL,
  NEW_PASSWORD_URL,
  PASSWORD_RESET_URL,
  PROFILE_URL,
  REGISTER_URL
} from "../constants/api";
import {
  USER_DELETE,
  USER_FETCH,
  USER_IN_PROCESS,
  USER_LOGGED_IN,
  USER_LOGGED_OUT,
  USER_OVERLAY_OPEN,
  USER_PASSWORD_NEW,
  USER_PASSWORD_REQUEST,
  USER_SET_MODE
} from "../constants/actionTypes";
import {requestAuth} from "../../../request";
import {ANALYTICS_SPECS} from "../../../shared/constants";
import {sendAnalyticsEvent} from "../../../shared/actions/analytics";
import { subscribeToTopic, unsubscribeFromTopic } from "../../../shared/modules/notification/actions/notificationsAction";

/**
 * Loges user out
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function logout() {
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      auth.clear();
      unsubscribeFromTopic(getState().user);
      resolve(
        dispatch({ type: USER_LOGGED_OUT })
      );

      sendAnalyticsEvent(ANALYTICS_SPECS.MEMBERS.NAME, ANALYTICS_SPECS.MEMBERS.EVENTS.LOGOUT);
    });
  };
}

/**
 * Loges user in
 * @param {string} email - login user`s email
 * @param {string} password - login user`s password}
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function login(email, password) {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      requestAuth()
        .post(LOGIN_URL)
        .send({user: email, hash: btoa(password)})
        .ok((res) => {
          return res.ok && res.body.token
        })
        .then((res) => {
          auth.store(res.body.token);
          resolve(
            dispatch({
              type: USER_LOGGED_IN,
              payload: { email: email }
            })
          );
        })
        .catch(reject);
    });
  };
}

/**
 * Gets user profile
 * @returns {function(dispatch, getState): Promise<any>} promised thunk to be dispatched
 */
export function getProfile() {
  return (dispatch, getState) => {
    const {user} = getState();

    if (user && 'inProcess' in user && user.inProcess) {
      return Promise.resolve(null);
    }

    if (user && 'isFetched' in user && user.isFetched) {
      return Promise.resolve(user)
    }

    dispatch({type: USER_IN_PROCESS});

    return requestAuth()
      .post(PROFILE_URL)
      .then((res) => {
        return dispatch(
          {
            type: USER_FETCH,
            payload: res.body
          }
        )
      })
      .then(() => {
        identifyMixPanel(`r${getState().user.cust_id}`);
        subscribeToTopic(getState().user);
        return getState().user;
      });
  };
}

/**
 * Sets new password to a user
 * @param {string} email - user`s email
 * @param {string} password1 - user`s password1
 * @param {string} password2 - user`s password2
 * @param {string} reset_token - reset token
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function setNewPassword(email, password1, password2, reset_token) {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      request
        .post(NEW_PASSWORD_URL)
        .send({
          email: email,
          password1: btoa(password1),
          password2: btoa(password2),
          reset_token: reset_token
        })
        .then(() => {
          resolve(dispatch({ type: USER_PASSWORD_NEW }));
        })
        .catch(reject);
    });
  };
}

/**
 * Signs up a user
 * @param {string} email - user`s email
 * @param {string} password - user`s password
 * @param {string} auth_key - auth key
 * @param {string} firstname - user`s firstname
 * @param {string} lastname - user`s lastname
 * @returns {Promise<any>} promise
 */
export function signUp(email, password, auth_key, firstname, lastname) {
  return Promise.resolve(
    request
      .post(REGISTER_URL)
      .send({
        hash: btoa(password),
        auth_key: auth_key,
        mail: email,
        prename: firstname,
        lastname: lastname
      })
  );
}

/**
 * Requests password for a user
 * @param {string} email - user`s email
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function requestPassword(email) {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      request
        .post(PASSWORD_RESET_URL)
        .send({ email: email })
        .then(() => {
          resolve(dispatch({ type: USER_PASSWORD_REQUEST }));
        })
        .catch(reject);
    });
  };
}

/**
 * Toggles an overlay for user
 * @param {boolean} click - whether overlay is toggled open or closed
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function toggleOverlay(click) {
  return (dispatch) => {
    dispatch({
      type: USER_OVERLAY_OPEN,
      payload: click
    })
  }
}

/**
 * Send delete account request to relatives api
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function deleteAccount() {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      requestAuth()
        .del(DELETE_ACCOUNT_URL)
        .ok((res) => {
          return res.accepted
        })
        .then(() => {
          resolve(dispatch({ type: USER_DELETE }));
        })
        .catch(reject);
    });
  }
}

/**
 * Set user utilization mode (B2B or B2C)
 * @param {string} mode - Utilization mode
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function setUserMode(mode) {
  return (dispatch) => {
    dispatch({
      type: USER_SET_MODE,
      payload: mode
    });
  };
}

/**
 * Check if EULA is accepted
 * @param {object} version app version
 * @returns {boolean} eula flag
 */
export function checkEULA(version) {
  return (localStorage.version === version.tag) && localStorage.eula_accepted;
}

/**
 * Set EULA accepted flag for given version
 * @param {object} version app version
 * @returns {void}
 */
export function acceptEULA(version) {
  localStorage.version = version.tag;
  localStorage.eula_accepted = true;
}
